export const VIDEO_PROVIDER = { Vimeo: 'vimeo', YouTube: 'youtube' } as const;
export type VideoProviderName = (typeof VIDEO_PROVIDER)[keyof typeof VIDEO_PROVIDER];

export interface ParsedVideoUrl {
  provider: VideoProviderName;
  id: string;
  hash: string | null;
}

const VIMEO_HOSTS = ['vimeo.com', 'www.vimeo.com'];
const VIMEO_PLAYER_HOST = 'player.vimeo.com';
const YOUTUBE_HOSTS = ['youtube.com', 'www.youtube.com', 'm.youtube.com'];
const YOUTUBE_SHORT_HOST = 'youtu.be';
const YOUTUBE_ID = /^[\w-]{11}$/;

const youtube = (id: string): ParsedVideoUrl | null =>
  (YOUTUBE_ID.test(id) ? { provider: VIDEO_PROVIDER.YouTube, id, hash: null } : null);

/** PHP's `parse_str` keeps the last value for a repeated query key; `URLSearchParams.get` keeps the first. */
const lastQueryValue = (params: URLSearchParams, key: string): string | null => params.getAll(key).at(-1) ?? null;

/**
 * `new URL()` normalises the path (collapsing `.`/`..`/`%2e` segments, turning `\` into `/`,
 * filling in a missing slash after the scheme, stripping embedded tabs and newlines) while PHP's
 * `parse_url()` leaves it as written, so read the path as written and compare the two.
 */
const rawPath = (url: string): string => {
  const schemeEnd = url.indexOf('://');
  if (schemeEnd === -1) {
    return '';
  }
  const authority = url.slice(schemeEnd + 3);
  const start = authority.search(/[/?#]/);
  return start === -1 || authority[start] !== '/' ? '' : authority.slice(start).split(/[?#]/)[0]!;
};

/**
 * The host as written, before `new URL()` strips embedded tabs and newlines out of it and maps
 * a lookalike (an IDN homograph such as `vimeo。com`) onto its punycode form.
 */
const rawHost = (url: string): string => {
  const schemeEnd = url.indexOf('://');
  if (schemeEnd === -1) {
    return '';
  }
  const authority = url.slice(schemeEnd + 3).split(/[/?#]/)[0]!;
  const host = authority.slice(authority.lastIndexOf('@') + 1);
  const portStart = host.lastIndexOf(':');
  return portStart === -1 ? host : host.slice(0, portStart);
};

/**
 * Reads a Vimeo or YouTube link into the video the player embeds, or `null` for a link it cannot
 * embed. The one parser both the validator's `video` check and KUI's player run; it mirrors
 * `App\Support\Video\VideoUrlParser`, and all three run the shared
 * `apps/phoenix/laravel/tests/Fixtures/video-urls.json` fixture.
 */
export const parseVideoUrl = (url: string): ParsedVideoUrl | null => {
  const trimmed = url.trim();

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  }
  catch {
    return null;
  }

  const host = parsed.hostname.toLowerCase();

  if (parsed.protocol !== 'https:' || parsed.pathname !== rawPath(trimmed) || host !== rawHost(trimmed).toLowerCase()) {
    return null;
  }

  const path = parsed.pathname;

  if (VIMEO_HOSTS.includes(host)) {
    const match = path.match(/^\/(\d+)(?:\/([A-Z0-9]+))?\/?$/i);
    return match ? { provider: VIDEO_PROVIDER.Vimeo, id: match[1]!, hash: match[2] ?? null } : null;
  }

  if (host === VIMEO_PLAYER_HOST) {
    const match = path.match(/^\/video\/(\d+)\/?$/);
    return match ? { provider: VIDEO_PROVIDER.Vimeo, id: match[1]!, hash: lastQueryValue(parsed.searchParams, 'h') || null } : null;
  }

  if (host === YOUTUBE_SHORT_HOST) {
    return youtube(path.replace(/^\//, ''));
  }

  if (YOUTUBE_HOSTS.includes(host)) {
    if (path === '/watch') {
      return youtube(lastQueryValue(parsed.searchParams, 'v') ?? '');
    }
    const match = path.match(/^\/(?:embed|shorts|live)\/([^/]+)\/?$/);
    return match ? youtube(match[1]!) : null;
  }

  return null;
};
